import {
  getListMediaQueryKey,
  getListSceneReplicasQueryKey,
  useListSceneReplicas,
  useReplicateScene as useReplicateSceneMutation,
  useSaveSceneClip,
} from '@reelplicate/api-client/media';
import type { ReplicateSceneDto, SceneReplicaDto } from '@reelplicate/api-client/model';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import type { Asset } from './types';

/** "0:02.4" */
export function timecode(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(1).padStart(4, '0')}`;
}

/** Save one scene as a clip, then refresh the library. */
export function useSaveClip(workspaceId: string) {
  const queryClient = useQueryClient();
  const mutation = useSaveSceneClip();
  const save = async (asset: Asset, scene: number) => {
    try {
      await mutation.mutateAsync({
        id: workspaceId,
        assetId: asset.id,
        scene,
        data: {},
      });
    } catch {
      return; // Shown as a toast.
    }
    await queryClient.invalidateQueries({ queryKey: getListMediaQueryKey(workspaceId) });
    toast.success(`Scene ${scene} saved as a clip`);
  };
  return {
    save,
    savingScene: mutation.isPending ? mutation.variables?.scene : undefined,
  };
}

const making = (replica: SceneReplicaDto) =>
  replica.status === 'QUEUED' || replica.status === 'RUNNING';

/**
 * A reel's scene replicas, the latest per scene. Asked again every few
 * seconds while one is being made; the library refreshes when one lands.
 */
export function useSceneReplicas(workspaceId: string, assetId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const { data } = useListSceneReplicas(workspaceId, assetId, {
    query: {
      enabled: enabled && Boolean(workspaceId),
      throwOnError: false,
      refetchInterval: (query) => (query.state.data?.some(making) ? 5_000 : false),
    },
  });
  const done = data?.filter((r) => r.status === 'DONE').length ?? 0;
  const seen = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (seen.current !== undefined && done > seen.current) {
      void queryClient.invalidateQueries({
        queryKey: getListMediaQueryKey(workspaceId),
      });
    }
    seen.current = done;
  }, [done, queryClient, workspaceId]);
  return useMemo(() => {
    const latest = new Map<number, SceneReplicaDto>();
    // Newest first: the first per scene is its latest.
    for (const replica of data ?? []) {
      if (!latest.has(replica.scene)) latest.set(replica.scene, replica);
    }
    return latest;
  }, [data]);
}

/** Start remaking one scene as an AI video. */
export function useReplicateScene(workspaceId: string) {
  const queryClient = useQueryClient();
  const mutation = useReplicateSceneMutation();
  const replicate = async (asset: Asset, scene: number, data: ReplicateSceneDto) => {
    try {
      await mutation.mutateAsync({ id: workspaceId, assetId: asset.id, scene, data });
    } catch {
      return false; // Shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getListSceneReplicasQueryKey(workspaceId, asset.id),
    });
    toast.success(`Replicating scene ${scene} - it takes a few minutes`);
    return true;
  };
  return { replicate, pending: mutation.isPending };
}
